/**
 * Планировщик миграций для автоматического запуска
 */

import { EventEmitter } from 'events';
import { MigrationRunner, MigrationRunnerConfig, MigrationExecutionInfo } from './MigrationRunner';
import { IMigrationManager } from '../types/migration-interfaces';

/**
 * Триггер для запуска миграций
 */
export type MigrationTrigger = 'startup' | 'scheduled' | 'manual' | 'version_change';

/**
 * Конфигурация планировщика
 */
export interface MigrationSchedulerConfig extends MigrationRunnerConfig {
  // Триггеры для автоматического запуска
  triggers?: MigrationTrigger[];
  
  // Интервал проверки ожидающих миграций (в мс)
  checkInterval?: number;
  
  // Задержка перед первой проверкой (в мс)
  initialDelay?: number;
  
  // Автоматически запускать при инициализации
  autoStart?: boolean;
  
  // Максимальное количество одновременных выполнений
  maxConcurrentExecutions?: number;
  
  // Уведомления о статусе
  notifications?: {
    onStart?: boolean;
    onComplete?: boolean;
    onError?: boolean;
  };
  
  // Хуки для событий
  hooks?: {
    beforeExecution?: () => Promise<boolean>;
    afterExecution?: (result: MigrationExecutionInfo) => Promise<void>;
    onError?: (error: Error) => Promise<void>;
  };
}

/**
 * Информация о задаче миграции
 */
export interface MigrationTask {
  id: string;
  trigger: MigrationTrigger;
  scheduledAt: Date;
  startedAt?: Date;
  completedAt?: Date;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  result?: MigrationExecutionInfo;
  error?: string;
}

/**
 * Планировщик миграций
 */
export class MigrationScheduler extends EventEmitter {
  private migrationManager: IMigrationManager;
  private runner: MigrationRunner;
  private config: MigrationSchedulerConfig;
  private tasks: Map<string, MigrationTask> = new Map();
  private checkTimer?: NodeJS.Timeout;
  private isStarted: boolean = false;
  private executionCount: number = 0;

  constructor(migrationManager: IMigrationManager, config: MigrationSchedulerConfig = {}) {
    super();
    this.migrationManager = migrationManager;
    this.config = {
      triggers: ['startup'],
      checkInterval: 60000, // 1 минута
      initialDelay: 1000,   // 1 секунда
      autoStart: true,
      maxConcurrentExecutions: 1,
      notifications: {
        onStart: true,
        onComplete: true,
        onError: true
      },
      ...config
    };
    
    this.runner = new MigrationRunner(migrationManager, config);
    this.setupEventHandlers();
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventHandlers(): void {
    this.runner.on('execution:start', (info) => {
      this.emit('execution:start', info);
      if (this.config.notifications?.onStart) {
        this.log(`Migration execution started: ${info.totalMigrations} migrations`);
      }
    });

    this.runner.on('execution:complete', (info) => {
      this.executionCount--;
      this.emit('execution:complete', info);
      
      if (this.config.notifications?.onComplete) {
        this.log(`Migration execution completed: ${info.completedMigrations}/${info.totalMigrations} successful`);
      }
      
      // Вызываем хук после выполнения
      this.config.hooks?.afterExecution?.(info).catch(error => {
        this.log(`Hook afterExecution failed: ${error.message}`);
      });
    });

    this.runner.on('migration:error', ({ migration, error }) => {
      this.emit('migration:error', { migration, error });
      
      if (this.config.notifications?.onError) {
        this.log(`Migration ${migration.id} failed: ${error.message}`);
      }
      
      // Вызываем хук ошибки
      this.config.hooks?.onError?.(error).catch(hookError => {
        this.log(`Hook onError failed: ${hookError.message}`);
      });
    });

    this.runner.on('log', ({ message }) => {
      this.emit('log', { message, source: 'runner' });
    });
  }

  /**
   * Запуск планировщика
   */
  async start(): Promise<void> {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;
    this.log('Migration scheduler started');

    // Запускаем миграции при старте если настроено
    if (this.config.triggers?.includes('startup')) {
      setTimeout(() => {
        this.scheduleTask('startup');
      }, this.config.initialDelay);
    }

    // Настраиваем периодическую проверку
    if (this.config.checkInterval && this.config.checkInterval > 0) {
      this.checkTimer = setInterval(() => {
        this.checkPendingMigrations();
      }, this.config.checkInterval);
    }

    this.emit('scheduler:started');
  }

  /**
   * Остановка планировщика
   */
  async stop(): Promise<void> {
    if (!this.isStarted) {
      return;
    }

    this.isStarted = false;
    
    // Останавливаем таймер
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = undefined;
    }

    // Отменяем текущие выполнения
    if (this.runner.isExecuting()) {
      await this.runner.cancel();
    }

    this.log('Migration scheduler stopped');
    this.emit('scheduler:stopped');
  }

  /**
   * Планирование задачи миграции
   */
  scheduleTask(trigger: MigrationTrigger): string {
    const taskId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const task: MigrationTask = {
      id: taskId,
      trigger,
      scheduledAt: new Date(),
      status: 'pending'
    };

    this.tasks.set(taskId, task);
    this.log(`Scheduled migration task: ${taskId} (trigger: ${trigger})`);
    
    // Запускаем задачу асинхронно
    this.executeTask(taskId).catch(error => {
      this.log(`Task execution failed: ${error.message}`);
    });

    return taskId;
  }

  /**
   * Выполнение задачи миграции
   */
  private async executeTask(taskId: string): Promise<void> {
    const task = this.tasks.get(taskId);
    if (!task) {
      return;
    }

    // Проверяем лимит одновременных выполнений
    if (this.executionCount >= this.config.maxConcurrentExecutions!) {
      this.log(`Max concurrent executions reached, postponing task: ${taskId}`);
      setTimeout(() => this.executeTask(taskId), 5000);
      return;
    }

    try {
      task.status = 'running';
      task.startedAt = new Date();
      this.executionCount++;

      this.log(`Executing migration task: ${taskId}`);
      this.emit('task:start', task);

      // Вызываем хук перед выполнением
      const shouldContinue = await this.config.hooks?.beforeExecution?.() ?? true;
      if (!shouldContinue) {
        task.status = 'cancelled';
        task.completedAt = new Date();
        this.log(`Task cancelled by beforeExecution hook: ${taskId}`);
        this.emit('task:cancelled', task);
        return;
      }

      // Выполняем миграции
      const result = await this.runner.runPendingMigrations();
      
      task.status = result.status === 'completed' ? 'completed' : 'failed';
      task.completedAt = new Date();
      task.result = result;

      this.log(`Task completed: ${taskId} (status: ${task.status})`);
      this.emit('task:complete', task);

    } catch (error) {
      task.status = 'failed';
      task.completedAt = new Date();
      task.error = error instanceof Error ? error.message : String(error);

      this.log(`Task failed: ${taskId} - ${task.error}`);
      this.emit('task:error', { task, error });

    } finally {
      this.executionCount--;
    }
  }

  /**
   * Проверка ожидающих миграций
   */
  private async checkPendingMigrations(): Promise<void> {
    if (!this.isStarted) {
      return;
    }

    try {
      const status = await this.migrationManager.checkStatus();
      
      if (status.pendingMigrations.length > 0) {
        this.log(`Found ${status.pendingMigrations.length} pending migrations`);
        
        // Запускаем миграции если настроено
        if (this.config.triggers?.includes('scheduled')) {
          this.scheduleTask('scheduled');
        }
      }
    } catch (error) {
      this.log(`Error checking pending migrations: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Ручной запуск миграций
   */
  async runNow(): Promise<string> {
    return this.scheduleTask('manual');
  }

  /**
   * Получение всех задач
   */
  getTasks(): MigrationTask[] {
    return Array.from(this.tasks.values());
  }

  /**
   * Получение задачи по ID
   */
  getTask(taskId: string): MigrationTask | undefined {
    return this.tasks.get(taskId);
  }

  /**
   * Отмена задачи
   */
  async cancelTask(taskId: string): Promise<boolean> {
    const task = this.tasks.get(taskId);
    if (!task) {
      return false;
    }

    if (task.status === 'running') {
      // Отменяем выполнение
      await this.runner.cancel();
      task.status = 'cancelled';
      task.completedAt = new Date();
      this.emit('task:cancelled', task);
      return true;
    }

    if (task.status === 'pending') {
      task.status = 'cancelled';
      task.completedAt = new Date();
      this.emit('task:cancelled', task);
      return true;
    }

    return false;
  }

  /**
   * Очистка старых задач
   */
  cleanupTasks(olderThan: Date): number {
    let removed = 0;
    
    for (const [taskId, task] of this.tasks.entries()) {
      if (task.completedAt && task.completedAt < olderThan) {
        this.tasks.delete(taskId);
        removed++;
      }
    }

    if (removed > 0) {
      this.log(`Cleaned up ${removed} old tasks`);
    }

    return removed;
  }

  /**
   * Получение статистики
   */
  getStats(): {
    totalTasks: number;
    pendingTasks: number;
    runningTasks: number;
    completedTasks: number;
    failedTasks: number;
    cancelledTasks: number;
  } {
    const tasks = Array.from(this.tasks.values());
    
    return {
      totalTasks: tasks.length,
      pendingTasks: tasks.filter(t => t.status === 'pending').length,
      runningTasks: tasks.filter(t => t.status === 'running').length,
      completedTasks: tasks.filter(t => t.status === 'completed').length,
      failedTasks: tasks.filter(t => t.status === 'failed').length,
      cancelledTasks: tasks.filter(t => t.status === 'cancelled').length
    };
  }

  /**
   * Проверка, запущен ли планировщик
   */
  isRunning(): boolean {
    return this.isStarted;
  }

  /**
   * Логирование
   */
  private log(message: string): void {
    if (this.config.debug) {
      console.log(`[MigrationScheduler] ${message}`);
    }
    this.emit('log', { message, source: 'scheduler', timestamp: new Date() });
  }
} 